import React from "react";
import api from "./api";

const STORAGE_KEY = "frontendDiagnostics";
const SESSION_KEY = "frontendDiagnosticsSessionId";
const MAX_BATCH_SIZE = 50;
const FLUSH_INTERVAL_MS = 5000;
const SLOW_EVENT_THRESHOLD_MS = 150;
const SLOW_SOCKET_HANDLER_THRESHOLD_MS = 100;
const LISTENER_WARNING_THRESHOLD = 10;

let enabled = false;
let initialized = false;
let eventBuffer = [];
let flushTimer = null;
let longTaskObserver = null;
let sessionId = null;
let socketInstrumented = false;
let lastMemoryReportAt = 0;

const socketListenerCounts = new Map();
const socketHandlers = new Map();

const now = () => (window.performance && performance.now ? performance.now() : Date.now());

const getBuildHash = () => {
  const script = Array.from(document.scripts).find(item => /\/static\/js\/main\..*\.js/.test(item.src));
  const match = script?.src?.match(/main\.([a-z0-9]+)\.js/i);
  return match?.[1] || "unknown";
};

const getBrowser = () => {
  const userAgent = navigator.userAgent || "";
  return userAgent.slice(0, 180);
};

const createSessionId = () => {
  const existing = sessionStorage.getItem(SESSION_KEY);
  if (existing) return existing;

  const value = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  sessionStorage.setItem(SESSION_KEY, value);
  return value;
};

export const isDiagnosticsEnabled = () => enabled;

export const hashDiagnosticId = value => {
  if (value === undefined || value === null) return undefined;

  const source = `${sessionId || "diagnostics"}:${String(value)}`;
  let hash = 0;

  for (let index = 0; index < source.length; index += 1) {
    hash = ((hash << 5) - hash) + source.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash).toString(36);
};

const sanitizeRoute = () => {
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  const cleanParams = new URLSearchParams();

  ["diag", "messageId", "messageID"].forEach(key => {
    if (params.has(key)) cleanParams.set(key, "1");
  });

  const query = cleanParams.toString();
  return `${pathname}${query ? `?${query}` : ""}`.slice(0, 180);
};

const serializeCounts = counts => {
  if (!counts) return undefined;

  const output = {};
  Object.entries(counts).forEach(([key, value]) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      output[key] = value;
    }
  });
  return output;
};

export const recordDiagnosticEvent = event => {
  if (!enabled || !event?.eventType) return;

  eventBuffer.push({
    sessionId,
    timestamp: new Date().toISOString(),
    route: sanitizeRoute(),
    browser: getBrowser(),
    buildHash: getBuildHash(),
    visibilityState: document.visibilityState,
    eventType: event.eventType,
    component: event.component,
    durationMs: typeof event.durationMs === "number" ? event.durationMs : undefined,
    counts: serializeCounts(event.counts),
    ticketIdHash: event.ticketIdHash,
    metadata: event.metadata
  });

  if (eventBuffer.length >= MAX_BATCH_SIZE) {
    flushDiagnostics();
  }
};

export const flushDiagnostics = async ({ keepalive = false } = {}) => {
  if (!enabled || eventBuffer.length === 0) return;

  const events = eventBuffer.splice(0, MAX_BATCH_SIZE);
  const token = localStorage.getItem("token");
  const cleanToken = token?.startsWith('"') && token?.endsWith('"')
    ? token.slice(1, -1)
    : token;

  try {
    if (keepalive && cleanToken && window.fetch) {
      await fetch(`${api.defaults.baseURL}/frontend-diagnostics`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${cleanToken}`
        },
        body: JSON.stringify({ events })
      });
      return;
    }

    await api.post("/frontend-diagnostics", { events }, {
      metadata: { skipDiagnostics: true }
    });
  } catch (err) {
    eventBuffer = events.concat(eventBuffer).slice(0, 200);
  }
};

const installLongTaskObserver = () => {
  if (!window.PerformanceObserver) return;

  try {
    longTaskObserver = new PerformanceObserver(list => {
      list.getEntries().forEach(entry => {
        recordDiagnosticEvent({
          eventType: "longtask",
          durationMs: entry.duration,
          metadata: {
            startTime: Math.round(entry.startTime)
          }
        });
      });
    });
    longTaskObserver.observe({ entryTypes: ["longtask"] });
  } catch (err) {
    longTaskObserver = null;
  }
};

const installEventTimingProbe = () => {
  ["click", "keydown", "input", "submit"].forEach(eventName => {
    window.addEventListener(
      eventName,
      event => {
        const startedAt = now();

        setTimeout(() => {
          const durationMs = now() - startedAt;
          if (durationMs < SLOW_EVENT_THRESHOLD_MS) return;

          recordDiagnosticEvent({
            eventType: "event",
            component: eventName,
            durationMs,
            metadata: {
              target: event.target?.tagName || "unknown"
            }
          });
        }, 0);
      },
      true
    );
  });
};

const installApiProbe = () => {
  api.interceptors.response.use(
    response => {
      const config = response.config || {};
      const startedAt = config.metadata?.diagnosticStartedAt;
      const url = config.url || "";

      if (startedAt && !url.includes("/frontend-diagnostics")) {
        recordDiagnosticEvent({
          eventType: "api",
          component: config.method?.toUpperCase() || "GET",
          durationMs: now() - startedAt,
          metadata: {
            url: url.replace(/\/\d+/g, "/:id"),
            status: response.status
          }
        });
      }

      return response;
    },
    error => {
      const config = error.config || {};
      const startedAt = config.metadata?.diagnosticStartedAt;
      const url = config.url || "";

      if (startedAt && !url.includes("/frontend-diagnostics")) {
        recordDiagnosticEvent({
          eventType: "api_error",
          component: config.method?.toUpperCase() || "GET",
          durationMs: now() - startedAt,
          metadata: {
            url: url.replace(/\/\d+/g, "/:id"),
            status: error?.response?.status || 0
          }
        });
      }

      return Promise.reject(error);
    }
  );

  api.interceptors.request.use(config => {
    if (!config.metadata?.skipDiagnostics && !String(config.url || "").includes("/frontend-diagnostics")) {
      config.metadata = {
        ...(config.metadata || {}),
        diagnosticStartedAt: now()
      };
    }
    return config;
  });
};

const installMemoryProbe = () => {
  if (!performance.memory) return;

  setInterval(() => {
    const current = Date.now();
    if (current - lastMemoryReportAt < 30000) return;
    lastMemoryReportAt = current;

    recordDiagnosticEvent({
      eventType: "memory",
      counts: {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
      }
    });
  }, 30000);
};

export const instrumentSocket = socket => {
  if (socketInstrumented || !socket) return socket;

  socketInstrumented = true;

  const originalOn = socket.on.bind(socket);
  const originalOff = socket.off.bind(socket);
  const originalEmit = socket.emit.bind(socket);

  const getWrappedHandler = (eventName, handler) => {
    if (typeof handler !== "function") return handler;

    if (!socketHandlers.has(eventName)) {
      socketHandlers.set(eventName, new WeakMap());
    }

    const eventHandlers = socketHandlers.get(eventName);
    const existing = eventHandlers.get(handler);
    if (existing) return existing;

    const wrapped = (...args) => {
      const startedAt = now();
      try {
        return handler(...args);
      } finally {
        const durationMs = now() - startedAt;
        if (durationMs >= SLOW_SOCKET_HANDLER_THRESHOLD_MS) {
          recordDiagnosticEvent({
            eventType: "socket_handler",
            component: eventName,
            durationMs,
            counts: Object.fromEntries(socketListenerCounts)
          });
        }
      }
    };

    eventHandlers.set(handler, wrapped);
    return wrapped;
  };

  socket.on = (eventName, handler) => {
    const currentCount = (socketListenerCounts.get(eventName) || 0) + 1;
    socketListenerCounts.set(eventName, currentCount);

    recordDiagnosticEvent({
      eventType: currentCount > LISTENER_WARNING_THRESHOLD ? "socket_listener_warning" : "socket_listener_on",
      component: eventName,
      counts: Object.fromEntries(socketListenerCounts)
    });

    return originalOn(eventName, getWrappedHandler(eventName, handler));
  };

  socket.off = (eventName, handler) => {
    if (eventName) {
      const currentCount = Math.max((socketListenerCounts.get(eventName) || 1) - 1, 0);
      socketListenerCounts.set(eventName, currentCount);

      recordDiagnosticEvent({
        eventType: "socket_listener_off",
        component: eventName,
        counts: Object.fromEntries(socketListenerCounts)
      });
    }

    if (!handler) {
      return originalOff(eventName);
    }

    const wrapped = socketHandlers.get(eventName)?.get(handler) || handler;
    return originalOff(eventName, wrapped);
  };

  socket.emit = (eventName, ...args) => {
    recordDiagnosticEvent({
      eventType: "socket_emit",
      component: eventName,
      counts: Object.fromEntries(socketListenerCounts)
    });

    return originalEmit(eventName, ...args);
  };

  window.dumpFrontendDiagnostics = () => ({
    enabled,
    sessionId,
    bufferedEvents: eventBuffer.length,
    socketListeners: Object.fromEntries(socketListenerCounts)
  });

  return socket;
};

export const useDiagnosticRender = (component, metadataFactory) => {
  const startedAt = enabled ? now() : 0;

  React.useEffect(() => {
    if (!enabled) return;

    const metadata = typeof metadataFactory === "function" ? metadataFactory() : metadataFactory;

    recordDiagnosticEvent({
      eventType: "render",
      component,
      durationMs: now() - startedAt,
      counts: metadata?.counts,
      ticketIdHash: metadata?.ticketIdHash,
      metadata: metadata?.metadata
    });
  });
};

export const initFrontendDiagnostics = () => {
  const params = new URLSearchParams(window.location.search);

  if (params.get("diag") === "1") {
    localStorage.setItem(STORAGE_KEY, "1");
  }

  enabled = localStorage.getItem(STORAGE_KEY) === "1";

  window.enableFrontendDiagnostics = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    window.location.reload();
  };

  window.disableFrontendDiagnostics = () => {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SESSION_KEY);
    window.location.reload();
  };

  window.dumpFrontendDiagnostics = () => ({
    enabled,
    sessionId,
    bufferedEvents: eventBuffer.length,
    socketListeners: Object.fromEntries(socketListenerCounts)
  });

  if (!enabled || initialized) return;

  initialized = true;
  sessionId = createSessionId();

  installLongTaskObserver();
  installEventTimingProbe();
  installApiProbe();
  installMemoryProbe();

  flushTimer = setInterval(() => {
    flushDiagnostics();
  }, FLUSH_INTERVAL_MS);

  window.addEventListener("pagehide", () => {
    if (flushTimer) clearInterval(flushTimer);
    flushDiagnostics({ keepalive: true });
    if (longTaskObserver) longTaskObserver.disconnect();
  });

  recordDiagnosticEvent({
    eventType: "diagnostics_started"
  });
};
